import { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog } from '../../../components/ui/dialog';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { useAuth } from '../../auth/auth-context';
import { boardApi, boardKeys } from '../../board/api';
import { TaskDetailDrawer } from '../../collaboration/components/task-detail-drawer';
import { TaskFormDialog } from './task-form-dialog';
import { TaskProgressSlot } from './task-progress-slot';

/**
 * Chi tiết một task, mở từ **Danh sách** — APM-TASK-i.
 *
 * ## Vì sao màn hình Danh sách trước đây không mở được gì
 *
 * `TaskListView` gọi `onOpen(taskId)` từ lúc dựng, và `TasksPanel` chuyển nó
 * lên URL (`?taskId=…`). Nhưng thứ **đọc** tham số ấy lại là `Board` — và
 * `Board` chỉ được dựng khi `view === 'board'`. Nên ở chế độ Danh sách, bấm vào
 * tên một việc đổi URL rồi không hiện gì: một cú bấm câm.
 *
 * Component này là nửa còn thiếu. Nó cố tình **không** dùng lại
 * `NotificationTaskPopup`: popup ấy chỉ-đọc có chủ đích (người đang đọc hộp thư
 * cần biết có chuyện gì, không phải sửa cấu trúc dự án), còn ở đây người dùng
 * đang đứng trong dự án của mình.
 *
 * ## Vì sao tự tải lại task
 *
 * `TaskListView` giữ danh sách của riêng nó với bộ lọc riêng, và một `TaskView`
 * lấy từ đó là ảnh chụp lúc lọc. `TaskDetailDrawer` thì hiện bình luận, tệp,
 * lịch sử — những thứ đổi liên tục. Tải theo `id` cho ra **một** nguồn, dùng
 * chung khoá cache với mọi chỗ khác mở cùng task ấy.
 */
export function TaskPopup({
  projectId,
  taskId,
  canEdit,
  canAssign = false,
  canApprove = false,
  onClose,
}: {
  readonly projectId: string;
  readonly taskId: string;
  /** `task:update` ở dự án này. */
  readonly canEdit: boolean;
  /** `task:assign` ở dự án này — mở ô "Người làm" trong form sửa. */
  readonly canAssign?: boolean;
  /** `ai_suggestion:approve` — mở phần duyệt trong form sửa. */
  readonly canApprove?: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const task = useQuery({
    queryKey: boardKeys.task(taskId),
    queryFn: () => boardApi.task(taskId),
  });

  const progress = useMutation({
    mutationFn: ({ progressPct, actualStartAt }: { progressPct: number; actualStartAt?: string }) =>
      boardApi.setProgress(taskId, progressPct, actualStartAt),
    onSuccess: () => toast.success(t('board.progressSaved')),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.task(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['board', projectId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['gantt', projectId], exact: false });
    },
  });

  if (!user) return null;

  if (editing && task.data) {
    return (
      <TaskFormDialog
        projectId={projectId}
        task={task.data}
        canApprove={canApprove}
        canAssign={canAssign}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onClose();
        }}
      />
    );
  }

  /*
    Đang tải và lỗi phải có lớp phủ của riêng chúng: `TaskDetailDrawer` đòi có
    `task` mới dựng được, mà lúc này chưa có. Không có lớp phủ thì cú bấm rơi
    xuống danh sách phía sau và người dùng mở nhầm một việc khác.
  */
  if (task.isPending || task.isError) {
    return (
      <Dialog label={t('notifications.taskPopupLabel')} onClose={onClose}>
        {task.isError ? (
          <ErrorState message={errorMessage(task.error, t)} onRetry={() => void task.refetch()} />
        ) : (
          <Skeleton className="h-40 w-full" />
        )}
      </Dialog>
    );
  }

  /*
    Quyền cấp dự án **và** quyền trên đúng việc này — APM-TASK-own. `canEdit`
    truyền vào chỉ nói "có được sửa việc trong dự án này không"; với CONTRIBUTOR
    thì câu trả lời còn tuỳ từng việc.
  */
  const editable = canEdit && (task.data.canEdit ?? true);

  return (
    <TaskDetailDrawer
      task={task.data}
      currentUserId={user.id}
      canEdit={editable}
      commentsDeletable={false}
      attachmentsUploadable
      onClose={onClose}
      {...(editable ? { onEdit: () => setEditing(true) } : {})}
      progress={
        <TaskProgressSlot
          task={task.data}
          canEdit={editable}
          onChange={(value, actualStartAt) =>
            progress.mutate({ progressPct: value, ...(actualStartAt ? { actualStartAt } : {}) })
          }
        />
      }
    />
  );
}
